import { forwardRef, useLayoutEffect, useMemo, useRef, type PropsWithChildren } from "react"

import { Box, SvgIcon as MUISvgIcon, Stack, Typography } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import parse, {
  attributesToProps,
  domToReact,
  type HTMLReactParserOptions,
} from "html-react-parser"

import { useLineClamp } from "@ggs/hooks"
import { gsap } from "@ggs/ui/local/gsap"
import { ContainerGrid, Link, type FC, type FCWC } from ".."

type FactsItemProps = {
  title: string
  value: string
}

type FactsOverviewType = {
  title: string
  subtitle: string
  description: string
  items: FactsItemProps[]
}

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if ("name" in domNode && "attribs" in domNode) {
      const { attribs, name, children } = domNode

      if (name === "p") {
        return (
          <>
            {domToReact(children, parseOptions)}
            <br />
            <br />
          </>
        )
      }

      if (name === "a") {
        const isInternalLink = attribs.href && attribs.href.startsWith("http://www.goway.com") || attribs.href.startsWith("https://www.goway.com")

        if (children.some((child) => child.type === "tag" && child.name === "img")) {
          return (
            <Link
              {...attributesToProps(attribs)}
              type="base"
              sx={{ fontWeight: "inherit", pb: 0.5 }}
              target={isInternalLink ? undefined : "_blank"}
            >
              {domToReact(children, parseOptions)}
            </Link>
          )
        }
        return (
          <Link
            {...attributesToProps(attribs)}
            sx={{ fontWeight: "inherit", pb: 0.5 }}
            target={isInternalLink ? undefined : "_blank"}
          >
            {domToReact(children, parseOptions)}
          </Link>
        )
      }
    }
  },
}

const Facts: FC<FactsOverviewType> = ({ title, subtitle, description, items }) => {
  const ref = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const body = useMemo(() => {
    // const cleanHtmlEntities = decode(content).replace(/&[#0-9a-z]+;/gi, "")
    return parse(description ?? "", parseOptions)
  }, [description])

  const facts = useMemo(() => {
    return items.filter(({ value }) => Boolean(value))
  }, [items])

  const { isClamped, isShowingMore, toggleIsShowingMore } = useLineClamp({ ref, titleRef })
  return (
    <FactsStylesWrapper>
      <AnimatedSvgIcon />
      <ContainerGrid
        rowGap={{ xs: 8, lg: 15 }}
        sx={(theme) => ({
          borderBottom: { md: `1px solid ${theme.palette["neutral.300"].main}` },
          py: { xs: 15, md: 45 },
          zIndex: 1,
        })}
        id="learn-more"
      >
        <FactsContent>
          <FactsSubtitle>{subtitle}</FactsSubtitle>
          <FactsTitle ref={titleRef}>{title}</FactsTitle>
          <Typography
            ref={ref}
            component="p"
            variant="body.md"
            sx={{
              ...(!isShowingMore && {
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 9,
                textOverflow: "ellipsis",
                fontSize: {
                xs: '16px',
                sm: '16px',
                md: undefined,
              },
              '& a': {
                fontSize: {
                  xs: '16px',
                  sm: '16px',
                  md: undefined,
                },
              },
              }),

              "& > br:nth-last-of-type(-n + 2)": {
                display: "none",
              },

              "& div": {
                paddingTop: (theme) => theme.spacing(3.5),
              },
            }}
          >
            {body}
          </Typography>
        </FactsContent>
        {facts.length > 0 && (
          <FactsWrapper>
            <Typography variant="body.sm">At a Glance</Typography>
            {facts.map(({ title, value }, i) => (
              <FactsItem key={i} title={title} value={value} />
            ))}
          </FactsWrapper>
        )}
        {isClamped && (
          <FactsContent>
            <Box>
              <Link
                variant="link.md"
                onClick={toggleIsShowingMore}
                aria-label={`Read more about ${title}`}
                sx={(theme) => ({
                  [theme.breakpoints.down("sm")]: {
                    padding: "11px 0px",
                    fontSize: "16px",
                    backgroundPositionY: "32px",
                  },
                })}
              >
                {isShowingMore ? "Read Less" : "Read More"}
              </Link>
            </Box>
          </FactsContent>
        )}
      </ContainerGrid>
    </FactsStylesWrapper>
  )
}

export default Facts

/** A sub-component for the Facts title */
const FactsTitle = forwardRef(({ children }: PropsWithChildren, ref) => (
  <Box ref={ref}>
    <Typography variant="heading.2xl" component="h2" mb={{ xs: 8.5, lg: 15 }}>
      {children}
    </Typography>
  </Box>
))
FactsTitle.displayName = "FactsTitle"

/** A sub-component for the Facts subtitle */
const FactsSubtitle: FCWC = ({ children }) => (
  <Typography variant="body.lg" component="h3" mb={{ xs: 5, lg: 10 }}>
    {children}
  </Typography>
)

const FactsContent: FCWC = ({ children }) => (
  <Stack justifyContent="flex-end" gridColumn={{ xs: "1 / -1", md: "2 / -2", lg: "2 / span 5" }}>
    <Box>{children}</Box>
  </Stack>
)

/** A sub-component for the Facts Items wrapper */
const FactsWrapper: FCWC = ({ children }) => (
  <Stack
    justifyContent="flex-end"
    gridColumn={{ xs: "1 / -1", md: "2 / -2", lg: "9 / span 3" }}
    sx={{
      order: { xs: 4, lg: "unset" },
      position: "relative",
      pt: { xs: 7, lg: 0 },
      zIndex: 1,
    }}
  >
    <Stack rowGap={7}>{children}</Stack>
  </Stack>
)

const FactsItem: FC<FactsItemProps> = ({ title, value }) => {
  return (
    <FactsStylesItem pt={2}>
      <Typography variant="body.sm" component="h2">{title}</Typography>
      <Typography variant="body.md">{value}</Typography>
    </FactsStylesItem>
  )
}

/** A sub-component for the animated svg in the bg  */
const AnimatedSvgIcon = () => {
  const theme = useTheme()
  const mainRef = useRef<HTMLDivElement>(null)
  const pathRef = useRef<SVGPathElement>(null)
  const pointRef = useRef<SVGGElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(pathRef.current, {
        strokeDashoffset: 786,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "top 75%",
          end: "+=200",
          scrub: true,
          // markers: true,
          invalidateOnRefresh: true,
        },
      })
      gsap.to(pointRef.current, {
        opacity: 1,
        scrollTrigger: {
          trigger: mainRef.current,
          start: "bottom 83%",
          end: "+=100",
          scrub: true,
          invalidateOnRefresh: true,
          // markers: true,
        },
      })
    }, mainRef)
    return () => ctx.revert()
  }, [mainRef, pathRef, pointRef])

  return (
    <Box
      ref={mainRef}
      sx={(theme) => ({
        width: 769,
        height: 260,
        position: "absolute",
        top: 53,
        display: { xs: "none", lg: "block" },
        overflow: "hidden",

        [theme.breakpoints.down(1718)]: {
          left: 949,
        },
        [theme.breakpoints.up(1718)]: {
          right: 0,
        },
      })}
    >
      <MUISvgIcon
        viewBox="0 0 770 261"
        sx={{
          width: 770,
          height: 261,
        }}
      >
        <path
          d="M769.308 11.979C723.059 7.854 674.88-2.106 629.461 3.58c-62.216 7.79-98.071 37.26-129.645 68.362-22.447 22.11-47.689 43.193-71.796 64.582-31.104 27.598-62.246 55.027-108.858 73.633-46.826 18.692-104.25 26.11-160.306 31.055-32.159 2.836-70.358 3.452-103.415.504"
          fill="none"
          stroke="#F05B2A"
          strokeWidth="2"
          strokeDasharray=".1 10"
          strokeLinecap="round"
        />
        <path
          ref={pathRef}
          d="M769.308 11.979C723.059 7.854 674.88-2.106 629.461 3.58c-62.216 7.79-98.071 37.26-129.645 68.362-22.447 22.11-47.689 43.193-71.796 64.582-31.104 27.598-62.246 55.027-108.858 73.633-46.826 18.692-104.25 26.11-160.306 31.055-32.159 2.836-70.358 3.452-103.415.504"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="5"
          strokeDasharray={786}
          strokeDashoffset={786 * 2}
        />
        <g ref={pointRef} opacity="0">
          <path
            fill="#FDEEE9"
            fillOpacity="0.4"
            d="M47.27 244.306c-4.413 12.499-18.122 19.053-30.621 14.64-12.499-4.413-19.053-18.123-14.64-30.621 4.413-12.499 18.123-19.053 30.622-14.64 12.498 4.413 19.053 18.123 14.64 30.621Z"
          />
          <path fill="#F05B2A" d="M29.212 238.13a4 4 0 1 1-7.544-2.665 4 4 0 0 1 7.544 2.665Z" />
        </g>
      </MUISvgIcon>
    </Box>
  )
}

/** styled components */
const FactsStylesWrapper = styled("section")(
  ({ theme }) => `
  width: 100%;
  position: relative;
  overflow-x: hidden;
  `
)

const FactsStylesItem = styled(Box)(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-top: 1px solid ${theme.palette["neutral.200"].main};
  `
)
